import { CreateRegisterResponse } from 'compliance-api';
import { useCallback, useState } from 'react';
import { useRegistersListQuery } from '@/modules/common/queries/registers.ts';
import CreateRegisterModal from '@/modules/admin-dashboard/components/modals/CreateRegisterModal.tsx';
import { SelectPaginate, SelectPaginateOption } from 'ui';
import { PlusBlueIcon } from 'ui/icons';

type baseProps = {
  label?: string;
  disabled?: boolean;
  className?: string;
  placeholder?: string;
  isClearable?: boolean;
  enableCreation?: boolean;
};

type singleProps = {
  isMulti: false;
  onChange: (options: SelectPaginateOption<number>) => void;
  defaultValue?: SelectPaginateOption<number>;
  value?: SelectPaginateOption<number> | null;
};

type multiProps = {
  isMulti: true;
  onChange: (options: SelectPaginateOption<number>[]) => void;
  defaultValue?: SelectPaginateOption<number>[];
  value?: SelectPaginateOption<number>[];
};

type RegisterSelectPaginateProps = baseProps & (singleProps | multiProps);

const RegisterSelectPaginate = ({
  label,
  disabled,
  value,
  isMulti,
  className,
  placeholder,
  defaultValue,
  isClearable,
  enableCreation,
  onChange,
}: RegisterSelectPaginateProps) => {
  const [isCreationOpen, setIsCreationOpen] = useState(false);
  const [cacheUniq, setCacheUniq] = useState(Math.random());

  const { fetchNextPage } = useRegistersListQuery({ page: 1, page_size: 15 }, true);

  const handleAsyncLoadRegisterOptions = useCallback(
    async (page: number, search?: string) => {
      const registersData = await fetchNextPage({ pageParam: { page, search } });
      const registers = registersData?.data?.pages?.at(-1)?.data || [];

      return registers.map(({ id, name }) => ({ label: name, value: id }));
    },
    [fetchNextPage],
  );

  const handleOnRegisterAdded = useCallback(
    (res: CreateRegisterResponse) => {
      setCacheUniq(Math.random());

      const newValue = { label: res.response.name, value: res.response.id };

      if (isMulti) {
        onChange([...(value || []), newValue]);
      } else {
        onChange(newValue);
      }
    },
    [isMulti, onChange, value],
  );

  const addedLastOption = enableCreation
    ? {
        label: (
          <div className="flex gap-2">
            <img src={PlusBlueIcon} alt="add" />
            <span>Add label</span>
          </div>
        ),
        onClick: () => {
          setIsCreationOpen(true);
        },
      }
    : undefined;
  return (
    <>
      <SelectPaginate
        cacheUniqs={[cacheUniq]}
        isMulti={isMulti}
        disabled={disabled}
        defaultValue={defaultValue}
        className={className}
        label={label}
        value={enableCreation ? value || null : value}
        isClearable={isClearable}
        placeholder={placeholder}
        onAsync={handleAsyncLoadRegisterOptions}
        onChange={options => onChange(options as any)}
        addedLastOption={addedLastOption}
      />
      {isCreationOpen && (
        <CreateRegisterModal
          open={isCreationOpen}
          onClose={() => setIsCreationOpen(false)}
          onSuccess={handleOnRegisterAdded}
        />
      )}
    </>
  );
};

export default RegisterSelectPaginate;
